import { IceAbility } from './IceAbility.js';
import { ThunderAbility } from './ThunderAbility.js';
import { MeteorAbility } from './MeteorAbility.js';
import { BeamAbility } from './BeamAbility.js';
import { SnareAbility } from './SnareAbility.js';
import { GlacierAbility } from './GlacierAbility.js';
import { RayAbility } from './RayAbility.js';
import { BackflowAbility } from './BackflowAbility.js';
import { PrismWakeAbility } from './PrismWakeAbility.js';
import { EventHorizonAbility } from './EventHorizonAbility.js';
import { RazorChoirAbility } from './RazorChoirAbility.js';
import { TidalVaultAbility } from './TidalVaultAbility.js';
import { SunwheelAbility } from './SunwheelAbility.js';
import { FaultFoldAbility } from './FaultFoldAbility.js';
import { BrambleOrreryAbility } from './BrambleOrreryAbility.js';
import { CataclysmFireballAbility } from './CataclysmFireballAbility.js';
import { RunicAegisAbility } from './RunicAegisAbility.js';
import { ShadowStepAbility } from './ShadowStepAbility.js';
import { CamouflageAbility } from './CamouflageAbility.js';
import { ELEMENTS } from '../config/settings.js';
import { ObjectPool } from '../utils/ObjectPool.js';
import { Matrix4 } from 'three';

/** Registry: adding an ability means adding one line here. */
const ABILITY_TYPES = {
  ice: IceAbility,
  thunder: ThunderAbility,
  meteor: MeteorAbility,
  beam: BeamAbility,
  snare: SnareAbility,
  glacier: GlacierAbility,
  ray: RayAbility,
  backflow: BackflowAbility,
  prism: PrismWakeAbility,
  horizon: EventHorizonAbility,
  razor: RazorChoirAbility,
  tide: TidalVaultAbility,
  sunwheel: SunwheelAbility,
  faultfold: FaultFoldAbility,
  bramble: BrambleOrreryAbility,
  cataclysm: CataclysmFireballAbility,
  runic: RunicAegisAbility,
  shadowstep: ShadowStepAbility,
  camouflage: CamouflageAbility
};

const MAX_CONCURRENT = 4;
const _identity = new Matrix4();

/**
 * Spawns, updates and recycles abilities.
 *
 * Instances are pooled per type: casting fifty times constructs at most a
 * handful of objects per ability, and every one of them keeps its meshes and
 * materials for the lifetime of the app. Nothing is built during a cast.
 *
 * `MAX_CONCURRENT` is shared across types, so mixing abilities retires the
 * oldest cast whichever element it was.
 */
export class AbilityManager {
  /**
   * @param {object} context shared systems handed to every ability:
   *   { scene, camera, environment, particles, lights, decals, bursts, shake, flash }
   */
  constructor(context) {
    this.ctx = context;
    this.active = [];
    this.selected = ELEMENTS[0];

    this.pools = new Map();
    for (const [element, Type] of Object.entries(ABILITY_TYPES)) {
      this.pools.set(
        element,
        new ObjectPool(() => {
          const ability = new Type(this.ctx);
          this.ctx.scene.add(ability.group);
          ability.group.visible = false;
          return ability;
        })
      );
    }
    this.prewarm();
  }

  /** Build the full supported overlap before input is enabled. */
  prewarm(count = MAX_CONCURRENT) {
    for (const pool of this.pools.values()) pool.prewarm(count);
  }

  /**
   * Temporarily expose one pooled instance of every shader variant (plus every
   * shared particle material) so App.load() can compile them behind the loader.
   * Returns a restoration callback and never advances gameplay state.
   */
  exposeForShaderWarmup() {
    const snapshots = [];
    const expose = (object) => {
      snapshots.push({ object, visible: object.visible, count: object.isInstancedMesh ? object.count : null });
      object.visible = true;
      if (object.isInstancedMesh && object.count === 0) {
        object.setMatrixAt(0, _identity);
        object.count = 1;
        object.instanceMatrix.needsUpdate = true;
      }
    };

    for (const pool of this.pools.values()) {
      const ability = pool.free.at(-1);
      if (!ability) continue;
      expose(ability.group);
      ability.group.traverse((object) => {
        if (object !== ability.group && object.material) expose(object);
      });
    }
    for (const system of this.ctx.particles.systems.values()) {
      const mesh = system.object3D;
      snapshots.push({ object: mesh, visible: mesh.visible, count: mesh.geometry.instanceCount });
      mesh.visible = true;
      mesh.geometry.instanceCount = Math.max(1, mesh.geometry.instanceCount);
    }

    return () => {
      for (let index = snapshots.length - 1; index >= 0; index--) {
        const { object, visible, count } = snapshots[index];
        object.visible = visible;
        if (object.isInstancedMesh) object.count = count;
        else if (object.geometry && count != null) object.geometry.instanceCount = count;
      }
    };
  }

  select(element) {
    if (!ABILITY_TYPES[element]) return;
    this.selected = element;
  }

  /**
   * Cast the selected ability along a line.
   *
   * A far cast takes the same three arguments and simply works from the far end
   * of that line — which is why adding zone targeting needed nothing here.
   *
   * @param {THREE.Vector3} origin     on the floor
   * @param {THREE.Vector3} direction  unit, flat
   * @param {number} distance          metres
   * @returns {import('./Ability.js').Ability|null}
   */
  cast(origin, direction, distance, element = this.selected) {
    if (!ABILITY_TYPES[element]) return null;

    // Retire the oldest cast rather than letting the scene grow without bound.
    if (this.active.length >= MAX_CONCURRENT) {
      const oldest = this.active.shift();
      oldest.destroy();
      this.pools.get(oldest.element).release(oldest);
    }

    const ability = this.pools.get(element).acquire();
    ability.spawn(origin, direction, distance);
    this.active.push(ability);
    return ability;
  }

  /** Acquire a pooled hold-to-cast ability and keep it live above the caster. */
  beginCharge(origin, element = this.selected) {
    const Type = ABILITY_TYPES[element];
    if (!Type) return null;
    if (this.active.length >= MAX_CONCURRENT) {
      const oldest = this.active.shift();
      oldest.destroy();
      this.pools.get(oldest.element).release(oldest);
    }
    const ability = this.pools.get(element).acquire();
    if (typeof ability.beginCharge !== 'function') {
      this.pools.get(element).release(ability);
      return null;
    }
    ability.beginCharge(origin);
    this.active.push(ability);
    return ability;
  }

  releaseCharge(ability, origin, direction, distance) {
    if (!ability || !this.active.includes(ability)) return false;
    return ability.release?.(origin, direction, distance) === true;
  }

  cancelCharge(ability) {
    const index = this.active.indexOf(ability);
    if (index < 0) return;
    this.active.splice(index, 1);
    ability.destroy();
    this.pools.get(ability.element).release(ability);
  }

  /** Most recent live instance of an element, including a residual anchor. */
  getActive(element) {
    for (let index = this.active.length - 1; index >= 0; index--) {
      if (this.active[index].element === element && this.active[index].isActive) return this.active[index];
    }
    return null;
  }

  /** Execute an ability-owned second activation without creating another cast. */
  reactivate(element) {
    const ability = this.getActive(element);
    if (!ability?.canReactivate || typeof ability.reactivate !== 'function') return null;
    return ability.reactivate() ? ability : null;
  }

  update(dt) {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const ability = this.active[i];
      ability.update(dt);
      if (ability.isFinished) {
        this.active.splice(i, 1);
        ability.destroy();
        this.pools.get(ability.element).release(ability);
      }
    }
  }

  /** Cancel everything currently in flight. */
  clear() {
    for (const ability of this.active) {
      ability.destroy();
      this.pools.get(ability.element).release(ability);
    }
    this.active.length = 0;
  }

  /** The most recent still-running cast — used to frame the camera. */
  get focus() {
    for (let i = this.active.length - 1; i >= 0; i--) {
      if (this.active[i].isActive) return this.active[i];
    }
    return null;
  }

  dispose() {
    this.clear();
    for (const pool of this.pools.values()) pool.dispose((ability) => ability.dispose());
    this.pools.clear();
  }
}
